import { useEffect, useRef } from 'react';

import styles from './GoogleSignInButton.module.css';

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

const GIS_SCRIPT_ID = 'google-identity-services';
const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

interface GoogleSignInButtonProps {
  onSuccess: (code: string) => void;
  onError?: () => void;
  label?: string;
  disabled?: boolean;
}

export function GoogleSignInButton({
  onSuccess,
  onError,
  label = 'Sign in with Google',
  disabled = false,
}: GoogleSignInButtonProps) {
  const clientRef = useRef<{ requestCode: () => void } | null>(null);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!CLIENT_ID) return;

    const initClient = () => {
      if (!window.google) return;
      clientRef.current = window.google.accounts.oauth2.initCodeClient({
        client_id: CLIENT_ID,
        scope: 'openid email profile',
        ux_mode: 'popup',
        callback: (response) => {
          if (response.error) {
            onErrorRef.current?.();
            return;
          }
          if (response.code) {
            onSuccessRef.current(response.code);
          }
        },
      });
    };

    // Script already loaded
    if (window.google) {
      initClient();
      return;
    }

    // Script tag already injected (concurrent mounts)
    if (document.getElementById(GIS_SCRIPT_ID)) {
      const existing = document.getElementById(GIS_SCRIPT_ID) as HTMLScriptElement;
      existing.addEventListener('load', initClient);
      return () => existing.removeEventListener('load', initClient);
    }

    const script = document.createElement('script');
    script.id = GIS_SCRIPT_ID;
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = initClient;
    document.body.appendChild(script);

    return () => {
      script.removeEventListener('load', initClient);
    };
  }, []);

  if (!CLIENT_ID) return null;

  const handleClick = () => {
    clientRef.current?.requestCode();
  };

  return (
    <button
      type="button"
      className={styles.button}
      onClick={handleClick}
      disabled={disabled}
      aria-label={label}
    >
      <span className={styles.icon} aria-hidden="true">
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
            fill="#4285F4"
          />
          <path
            d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
            fill="#34A853"
          />
          <path
            d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
            fill="#FBBC05"
          />
          <path
            d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
            fill="#EA4335"
          />
        </svg>
      </span>
      <span className={styles.label}>{label}</span>
    </button>
  );
}
